import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { toThunkRejectValue } from "../../../../Common/isRequestCanceled";
import {
  REPORTS_BASE_URL,
  DISCOUNT_REPORT_FACT_API,
} from "../../../../Constants/Config";

const initialState = {
  loading: false,
  discountSalesData: {},
  topDiscounts: {},
  topDiscountsByAmount: {},
  byLocation: [],
  status: false,
  successMessage: "",
  error: "",
};

let cancelTokenSource;

// One request replaces the three PHP calls this report used to fire
// (discount_sales_report_list + top_sale_discount_sold +
// top_discounts_by_amount). All three returned slices of the same six
// aggregates over the same window, so the fact endpoint computes them once and
// returns all three payloads under the same keys the UI already reads.
export const fetchDiscountSales = createAsyncThunk(
  "discountSalesReportSlice/fetchDiscountSales",
  async (data, { rejectWithValue }) => {
    if (cancelTokenSource) {
      cancelTokenSource.cancel("Previous request canceled");
    }

    // Create a new cancel token
    cancelTokenSource = axios.CancelToken.source();

    try {
      const { token, ...dataNew } = data;

      // The fact endpoint is FastAPI and reads Form() fields, so the body is a
      // real multipart FormData and the Content-Type header is left to axios so
      // it can generate the boundary.
      const formData = new FormData();
      const append = (key, value) => {
        if (value === undefined || value === null || value === "") return;
        formData.append(key, value);
      };

      // merchant_id must stay the LOGGED-IN merchant: auth validates the token
      // against it, so sending a selected outlet here is a 401. outlet_ids is
      // what actually scopes the report.
      append("merchant_id", dataNew?.merchant_id);
      // DashDateRangeComponent's Outlet picker is multi-select and emits
      // `stores` as a comma-joined merchant_id list; that IS the multi-location
      // filter. Fall back to the logged-in merchant so a single-store login
      // stays scoped rather than widening to everything.
      append(
        "outlet_ids",
        dataNew?.outlet_ids || dataNew?.stores || dataNew?.merchant_id
      );
      append("start_date", dataNew?.start_date);
      append("end_date", dataNew?.end_date);
      append("discount_type", dataNew?.discount_type);
      append("token_id", dataNew?.token_id);
      append("login_type", dataNew?.login_type);
      append("session_id", dataNew?.session_id);

      const res = await axios.post(
        REPORTS_BASE_URL + DISCOUNT_REPORT_FACT_API,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          cancelToken: cancelTokenSource.token,
        }
      );

      if (res.data.status === true) {
        return {
          data: res.data.discount_data,
          topDiscounts: res.data.top_discounts,
          topDiscountsByAmount: res.data.top_discounts_by_amount,
          byLocation: res.data.by_location,
          status: true,
        };
      }
      return {
        data: null,
        topDiscounts: {},
        topDiscountsByAmount: {},
        byLocation: [],
        status: false,
      };
    } catch (error) {
      const customError = {
        message: toThunkRejectValue(error).message,
        status: toThunkRejectValue(error).status,
        data: error.response ? error.response.data : null,
      };
      return rejectWithValue(customError);
    }
  }
);

const discountSalesReportSlice = createSlice({
  name: "discountSalesReportSlice",
  initialState,
  reducers: {
    updateStateOfDiscount: (state) => {
      state.discountSalesData = {};
      state.topDiscounts = {};
      state.topDiscountsByAmount = {};
      state.byLocation = [];
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchDiscountSales.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(fetchDiscountSales.fulfilled, (state, action) => {
      state.loading = false;
      state.discountSalesData = action.payload.data;
      state.topDiscounts = action.payload.topDiscounts;
      state.topDiscountsByAmount = action.payload.topDiscountsByAmount;
      state.byLocation = action.payload.byLocation;
      state.status = action.payload.status;
      state.error = "";
    });
    builder.addCase(fetchDiscountSales.rejected, (state, action) => {
      state.loading = false;
      state.discountSalesData = {};
      state.topDiscounts = {};
      state.topDiscountsByAmount = {};
      state.byLocation = [];
      state.error = action.error.message;
    });
  },
});

export const { updateStateOfDiscount } = discountSalesReportSlice.actions;
export default discountSalesReportSlice.reducer;
